// Enable/disable position input based on placement select
$(document).on('change', '.stockImagePlacement', function () {
  const $row = $(this).closest('.stock-image-card');
  const $position = $row.find('.stockImagePosition');
  if ($(this).val() === 'inline') {
    $position.prop('disabled', false);
    // Default to 1 if empty or not a positive integer
    const val = Number.parseInt(String($position.val()), 10);
    if (!val || val < 1) $position.val(1);
  } else {
    $position.prop('disabled', true).val('');
  }
});

// Block characters that are invalid for a positive integer in number inputs
$(document).on('keydown', '.stockImagePosition', (e) => {
  const nativeEvent = e.originalEvent;
  if (!nativeEvent) return;
  if (['-', '+', 'e', 'E', '.'].includes(nativeEvent.key)) {
    e.preventDefault();
  }
});

// Prevent pasting negative numbers or non-integers
$(document).on('paste', '.stockImagePosition', (e) => {
  const clipboardData = (e.originalEvent as ClipboardEvent | undefined)?.clipboardData;
  const text = clipboardData?.getData('text') ?? '';
  const num = Number.parseInt(text, 10);
  if (Number.isNaN(num) || num < 1) {
    e.preventDefault();
  }
});

// Clamp position to a positive integer >= 1 on input/change/blur
$(document).on('input change blur', '.stockImagePosition', function () {
  const $input = $(this);
  const raw = Number.parseInt(String($input.val()), 10);
  if (Number.isNaN(raw) || raw < 1) {
    $input.val(1);
  } else {
    $input.val(raw);
  }
});

// When "available for purchase" is toggled, auto-suggest the same value for
// "apply watermark" (the user can override it independently).
$(document).on('change', '.stockImageAvailableForPurchase', function () {
  const $row = $(this).closest('.stock-image-card');
  const isChecked = $(this).prop('checked') as boolean;
  const $watermark = $row.find('.stockImageApplyWatermark');
  $watermark.prop('checked', isChecked);
});

interface PolicyChecks {
  allChecked: string;
  includeChecked: string;
  excludeChecked: string;
}

type TagPolicy = {
  inc?: string[];
  exc?: string[];
};

type CreateStockImageParams = {
  imgUrl: string;
  imgTmbUrl: string;
  imgSize: number;
  imgTmbSize: number;
  dateStart?: string;
  dateEnd?: string;
  policy?: TagPolicy;
  uid?: string;
  renderStyle?: string;
  placement?: string;
  position?: string | number | null;
  url?: string | null;
  availableForPurchase?: boolean;
  applyWatermark?: boolean;
  canBeDownloaded?: boolean;
  canBeMaximized?: boolean;
  showFavorites?: boolean;
  displayTargets?: string[];
};

type CreateStockImageRowParams = {
  uidRow: string;
  imgUrl: string;
  thumbHtml: string;
  imgSize: number;
  imgTmbSize: number;
  stockImageInfoHtml: string;
  dateStart?: string;
  dateEnd?: string;
  policyChecks: PolicyChecks;
  tags: string;
  tagInputId: string;
  renderStyle?: string;
  placement?: string;
  position?: string | number | null;
  url?: string | null;
  availableForPurchase?: boolean;
  applyWatermark?: boolean;
  canBeDownloaded?: boolean;
  canBeMaximized?: boolean;
  showFavorites?: boolean;
  displayTargets?: string[];
};

interface StockImage {
  uid: string;
  imgUrl: string;
  imgTmbUrl: string;
  imgSize: string;
  imgTmbSize: string;
  dateStart: string;
  dateEnd: string;
  renderStyle?: string;
  placement?: string;
  position?: string | number | null;
  url?: string | null;
  policy: TagPolicy;
  availableForPurchase?: boolean;
  applyWatermark?: boolean;
  canBeDownloaded?: boolean;
  canBeMaximized?: boolean;
  showFavorites?: boolean;
  displayTargets?: string[];
}

type StockImageInfo = {
  fileSize: number;
  tmbSize: number;
};

const BAD_THUMBNAIL_TEXT = 'Need<br> custom<br> thumbnail!';
const STOCK_IMAGE_LIST_ID = 'stockImageList';

const TAG_INPUT_PARAMS = {
  defaultText: 'comma-separated list of tags',
  width: 'auto',
  pattern: /^[a-zA-Z0-9-_]+$/,
  height: '80px',
};

export function createStockImageRow({
  imgUrl,
  imgTmbUrl,
  imgSize,
  imgTmbSize,
  dateStart,
  dateEnd,
  policy = {},
  uid,
  renderStyle = '',
  placement = '',
  position = '',
  url = '',
  availableForPurchase = false,
  applyWatermark = false,
  canBeDownloaded = false,
  canBeMaximized = true,
  showFavorites = false,
  displayTargets = ['webapp', 'kiosk'],
}: CreateStockImageParams) {
  const tbody = getStockImageList();

  const randomUid = crypto.randomUUID().replaceAll('-', '').substring(0, 13);
  const uidRow = uid || 'new';
  const tagInputId = `stockImageTags_${uidRow}_${randomUid}`;
  const policyObj = parseStockImagePolicy(policy);
  const tags = policyObj.tagList.join();
  const thumbHtml = getThumbHTML(imgTmbUrl, imgTmbSize);
  const stockImageInfoHtml = getStockImageInfoHtml({ fileSize: imgSize, tmbSize: imgTmbSize });
  const policyChecks = policyObj as PolicyChecks;

  const row = createStockImageRowHtml({
    uidRow,
    imgUrl,
    thumbHtml,
    imgSize,
    imgTmbSize,
    stockImageInfoHtml,
    dateStart,
    dateEnd,
    policyChecks,
    tags,
    tagInputId,
    renderStyle,
    placement,
    position,
    url,
    availableForPurchase,
    applyWatermark,
    canBeDownloaded,
    canBeMaximized,
    showFavorites,
    displayTargets,
  });
  tbody!.prepend(row);

  activateTagsInput(tagInputId, tags);
  initTagContainerState(row, policyObj.allChecked);
  setupTagButtons(row, tagInputId);
}

export function stockTabCollectAndValidateDataAddOrEditEvent() {
  const stockImagesRows = $('#stockImageList .stock-image-card');
  const stockImagesEnable = $('#stockImagesEnable').prop('checked');
  const stockImages: StockImage[] = [];
  stockImagesRows.each(function () {
    const $this = $(this);
    stockImages.push({
      uid: $this.data('uid') as string,
      imgUrl: $this.find('.stockImageImgUrl').val() as string,
      imgTmbUrl: $this.find('.stockImageTmb').prop('src') as string,
      imgSize: $this.find('.stockImageSize').val() as string,
      imgTmbSize: $this.find('.stockImageTmbSize').val() as string,
      dateStart: $this.find('.dateStart').val() as string,
      dateEnd: $this.find('.dateEnd').val() as string,
      renderStyle: $this.find('.stockImageRenderStyle').val() as string,
      placement: $this.find('.stockImagePlacement').val() as string,
      position: $this.find('.stockImagePosition').val() as string,
      url: normalizeUrl($this.find('.stockImageUrl').val() as string),
      policy: toPolicyObject($this),
      availableForPurchase: $this.find('.stockImageAvailableForPurchase').prop('checked') as boolean,
      applyWatermark: $this.find('.stockImageApplyWatermark').prop('checked') as boolean,
      canBeDownloaded: $this.find('.stockImageCanBeDownloaded').prop('checked') as boolean,
      canBeMaximized: $this.find('.stockImageCanBeMaximized').prop('checked') as boolean,
      showFavorites: $this.find('.stockImageShowFavorites').prop('checked') as boolean,
      displayTargets: $this
        .find('.stockImageDisplayTarget:checked')
        .map(function () {
          return $(this).val() as string;
        })
        .get(),
    });
  });

  return {
    stockImagesEnable,
    stockImages,
  };
}

// eslint-disable-next-line no-undef
export function updateStockImageInfo(card: JQuery<HTMLElement>) {
  if (!card) return;

  const obj: StockImageInfo = {
    fileSize: Number(card.find('.stockImageSize').val()) || 0,
    tmbSize: Number(card.find('.stockImageTmbSize').val()) || 0,
  };
  const newText = getStockImageInfoHtml(obj);
  card.find('.stock-image-file-info').prop('innerHTML', newText);
}

export function getThumbHTML(imgTmbUrl: string, imgTmbSize: number) {
  let thumbCls = 'stock-thumb';
  let badThumbHtml = '';

  if (!imgTmbUrl) {
    thumbCls = 'stock-thumb-hidden';
    badThumbHtml = `<br><span class=''>${BAD_THUMBNAIL_TEXT}</span>`;
  }

  return `
    <img class='${thumbCls} stockImageTmb' src='${imgTmbUrl}'
      onload = "occoEvent.setStockImageTmbLoader($(this).closest('.stock-image-card'))"
      alt=""/>${badThumbHtml}<input type='hidden' class='stockImageTmbSize' value='${imgTmbSize}' >
    `;
}

export const resetStockImagesList = () => {
  const list = getStockImageList();
  if (list) {
    list.innerHTML = '';
  }
};

function getStockImageInfoHtml(info: StockImageInfo): string {
  if (!info) return '';

  const parts: string[] = [];
  if (info.fileSize) {
    parts.push(`<span class="stock-image-info-item">File: ${new Intl.NumberFormat().format(info.fileSize)} KB</span>`);
  }
  if (info.tmbSize) {
    parts.push(
      `<span class="stock-image-info-item">Thumbnail: ${new Intl.NumberFormat().format(info.tmbSize)} KB</span>`
    );
  }
  return parts.join('');
}

function formatDateInputValue(date?: string): string {
  return date ? new Date(date).toISOString().split('T')[0] : '';
}

function getLoaderClass(imgTmbSize: number): string {
  return imgTmbSize ? 'stock-thumb' : 'stock-thumb-hidden';
}

function getThumbnailActionText(imgTmbSize: number): string {
  return imgTmbSize > 0 ? 'change' : 'add';
}

function getSelectedAttr(value: string, selectedValue: string): string {
  return value === selectedValue ? ' selected' : '';
}

function getCheckedAttr(isChecked: boolean): string {
  return isChecked ? 'checked' : '';
}

function getPositionInputState(placement: string, position: string | number | null | undefined) {
  if (placement === 'inline') {
    const posNum = Number(position);
    return {
      value: String(Math.max(posNum, 1)),
      disabledAttr: '',
    };
  }

  return {
    value: '',
    disabledAttr: 'disabled',
  };
}

function getDisplayTargetChecked(displayTargets: string[], target: string): string {
  return getCheckedAttr(displayTargets.includes(target));
}

function buildStockImageRowInnerHtml({
  uidRow,
  imgUrl,
  thumbHtml,
  loaderCls,
  imgSize,
  thumbActionText,
  stockImageInfoHtml,
  startDateInputId,
  formattedDateStart,
  endDateInputId,
  formattedDateEnd,
  renderStyle,
  placement,
  positionInput,
  url,
  availableForPurchaseChecked,
  watermarkChecked,
  canBeDownloadedChecked,
  canBeMaximizedChecked,
  showFavoritesChecked,
  webappChecked,
  kioskChecked,
  rowRand,
  allChecked,
  includeChecked,
  excludeChecked,
  tagInputId,
  tags,
}: {
  uidRow: string;
  imgUrl: string;
  thumbHtml: string;
  loaderCls: string;
  imgSize: number;
  thumbActionText: string;
  stockImageInfoHtml: string;
  startDateInputId: string;
  formattedDateStart: string;
  endDateInputId: string;
  formattedDateEnd: string;
  renderStyle: string;
  placement: string;
  positionInput: { value: string; disabledAttr: string };
  url: string | null | undefined;
  availableForPurchaseChecked: string;
  watermarkChecked: string;
  canBeDownloadedChecked: string;
  canBeMaximizedChecked: string;
  showFavoritesChecked: string;
  webappChecked: string;
  kioskChecked: string;
  rowRand: string;
  allChecked: string;
  includeChecked: string;
  excludeChecked: string;
  tagInputId: string;
  tags: string;
}) {
  return `
      <div class='card-body'>
        <div class='stock-image-card__header'>
          <div>
            <h6 class='stock-image-card__title'>Media item</h6>
          </div>
          <button type='button' title='Delete stock media row' class='btn btn-outline-danger btn-sm stock-image-card__delete SUPERUSER'
              onclick='occoEvent.removeStockImage($(this));'>
            <i class='fas fa-trash-alt' aria-hidden='true'></i>
          </button>
        </div>
        <div class='row stock-image-card__grid'>
          <div class='col-lg-4 stock-image-card__section'>
            <div class='stock-image-card__section-title'>Media and link</div>
            <p class='stock-image-card__section-help'>Preview the item, update its thumbnail, and optionally link it to another page. Stock media supports JPEG images and MP4 videos.</p>
            <div class='stock-image-thumb-wrap centered'>
              <a class='stockImageLink' target='_blank' rel='noopener noreferrer' href='${imgUrl}' title='Open original media'>
                <img src='img/load.gif' id='tmbLoader' class='${loaderCls}' alt=''/>
                <span id='stock-image-tmb'>${thumbHtml}</span>
              </a>
            </div>
            <div class='stock-image-file-info' id='stockImageInfo_${uidRow}'>${stockImageInfoHtml}</div>
            <div class='stock-image-promo-url form-group mb-3 mt-3'>
              <label class='stock-image-label' for='stockImageUrl_${uidRow}'>Target URL</label>
              <input
                type='url'
                id='stockImageUrl_${uidRow}'
                class='form-control form-control-sm stockImageUrl'
                value='${url ?? ''}'
                placeholder='https://example.com/offer'
                autocomplete='off'
                aria-label='Target URL'
              >
              <small class='stock-image-field-help'>Add a destination if clicking this item should open a page or offer.</small>
            </div>
            <div class='stock-image-media-actions'>
              <button
                type='button'
                title='Upload thumbnail'
                class='btn btn-outline-primary btn-sm fullWidth SUPERUSER'
                id='uploadStockImageTmbButton'
                onclick='occoEvent.startUploadStockImageTmb($(this));'
              >${thumbActionText} thumbnail</button>
            </div>
          </div>
          <div class='col-lg-4 stock-image-card__section'>
            <div class='stock-image-card__section-title'>When and where it shows</div>
            <p class='stock-image-card__section-help'>Choose when this item is active and how it should be placed in the experience.</p>
            <div class='form-group mb-3'>
              <label class='stock-image-label' for='${startDateInputId}'>Start date</label>
              <input id='${startDateInputId}' type='date' class='dateStart form-control form-control-sm' title='Start date' value='${formattedDateStart}'>
              <small class='stock-image-field-help'>Leave empty to start showing it right away.</small>
            </div>
            <div class='form-group mb-3'>
              <label class='stock-image-label' for='${endDateInputId}'>End date</label>
              <input id='${endDateInputId}' type='date' class='dateEnd form-control form-control-sm' title='End date' value='${formattedDateEnd}'>
              <small class='stock-image-field-help'>Leave empty if it should stay visible with no end date.</small>
            </div>
            <div class='form-group mb-3'>
              <label class='stock-image-label'>
                Render style
                <i class='fas fa-question-circle stock-image-render-style-info ml-1'
                  title='For Full row, use a 2:1 image (e.g. 3000 × 1500 px). Narrower images will appear cropped in the webapp album.'
                  aria-label='Render style help'></i>
              </label>
              <select class='form-control form-control-sm stockImageRenderStyle'>
                <option value='thumb'${getSelectedAttr(renderStyle, 'thumb')}>Thumbnail</option>
                <option value='full_row'${getSelectedAttr(renderStyle, 'full_row')}>Full row</option>
              </select>
              <small class='stock-image-field-help'>Choose how large this item should appear in the layout. For Full row, a 2:1 image (e.g. 3000 × 1500 px) gives the best result.</small>
            </div>
            <div class='form-row'>
              <div class='form-group col-sm-7 mb-0'>
                <label class='stock-image-label'>Placement</label>
                <select class='form-control form-control-sm stockImagePlacement'>
                  <option value='top'${getSelectedAttr(placement, 'top')}>Top</option>
                  <option value='inline'${getSelectedAttr(placement, 'inline')}>Inline</option>
                </select>
                <small class='stock-image-field-help'>Use inline if this item should appear within the content flow.</small>
              </div>
              <div class='form-group col-sm-5 mb-0'>
                <label class='stock-image-label'>Position</label>
                <input
                  type='number'
                  min='1'
                  step='1'
                  class='stockImagePosition form-control form-control-sm'
                  placeholder='1'
                  aria-label='Inline position'
                  value='${positionInput.value}'
                  ${positionInput.disabledAttr}
                >
              </div>
            </div>
          </div>
          <div class='col-lg-4 stock-image-card__section'>
            <div class='stock-image-card__section-title'>Permissions and targeting</div>
            <p class='stock-image-card__section-help'>Choose what users can do with this item and who should see it.</p>
            <div class="stock-image-meta-container">
              <div class='stock-image-check-list'>
                <label class="stock-image-check-item">
                  <input
                    type="checkbox"
                    class="stockImageAvailableForPurchase"
                    aria-label="Available for purchase"
                    ${availableForPurchaseChecked}
                  >
                  <span>Available for purchase
                    <i class='fas fa-question-circle ml-1'
                      title='Mark this item as available to purchase in the event store.'
                      aria-label='Available for purchase help'></i>
                  </span>
                </label>
                <label class="stock-image-check-item stock-image-watermark-container">
                  <input type="checkbox" class="stockImageApplyWatermark" aria-label="Apply watermark" ${watermarkChecked}>
                  <span>Apply watermark
                    <i class='fas fa-question-circle ml-1'
                      title='Overlay the event watermark on this item for unpurchased views.'
                      aria-label='Apply watermark help'></i>
                  </span>
                </label>
                <label class="stock-image-check-item">
                  <input type="checkbox" class="stockImageCanBeDownloaded" aria-label="Can be downloaded" ${canBeDownloadedChecked}>
                  <span>Can be downloaded</span>
                </label>
                <label class="stock-image-check-item">
                  <input type="checkbox" class="stockImageCanBeMaximized" aria-label="Can be maximized" ${canBeMaximizedChecked}>
                  <span>Can be maximized</span>
                </label>
                <label class="stock-image-check-item">
                  <input type="checkbox" class="stockImageShowFavorites" aria-label="Show favorites" ${showFavoritesChecked}>
                  <span>Show favorites</span>
                </label>
              </div>
              <div class='form-group mb-3'>
                <label class='stock-image-label d-block'>Display on</label>
                <label class="stock-image-check-item stock-image-check-item--inline">
                  <input type="checkbox" class="stockImageDisplayTarget" value="webapp" aria-label="Display on webapp" ${webappChecked}>
                  <span>Webapp</span>
                </label>
                <label class="stock-image-check-item stock-image-check-item--inline">
                  <input type="checkbox" class="stockImageDisplayTarget" value="kiosk" aria-label="Display on kiosk" ${kioskChecked}>
                  <span>Kiosk</span>
                </label>
              </div>
              <div class="tag-policy-container" data-uid="${uidRow}">
                <label class='stock-image-label d-block mb-2'>Tag policy</label>
                <label class='stock-image-policy-option'><input type="radio" name="tagPolicy_${rowRand}" value="all" class="tag-policy-radio" ${allChecked}> Show always</label>
                <label class='stock-image-policy-option'><input type="radio" name="tagPolicy_${rowRand}" value="include" class="tag-policy-radio" ${includeChecked}> Show only when matching tags are found</label>
                <label class='stock-image-policy-option'><input type="radio" name="tagPolicy_${rowRand}" value="exclude" class="tag-policy-radio" ${excludeChecked}> Show only when matching tags are not found</label>
                <div id="stockImageTagsCont_${rowRand}" class="tags-input-container">
                  <input type="text" id="${tagInputId}" class="form-control form-control-sm" data-role="tagsinput" value="${tags}">
                </div>
                <small class='stock-image-field-help'>Enter comma-separated tags if this item should be shown only for certain content.</small>
              </div>
            </div>
          </div>
        </div>
        <input type='hidden' id='stockImageUid_${uidRow}' value='${uidRow}' >
        <input type='hidden' class='stockImageSize' value='${imgSize}' >
        <input type='hidden' class='stockImageImgUrl' value='${imgUrl}' >
      </div>
    `;
}

function createStockImageRowHtml({
  uidRow,
  imgUrl,
  thumbHtml,
  imgSize,
  imgTmbSize,
  stockImageInfoHtml,
  dateStart,
  dateEnd,
  policyChecks,
  tags,
  tagInputId,
  renderStyle = '',
  placement = '',
  position = '',
  url = '',
  availableForPurchase = false,
  applyWatermark = false,
  canBeDownloaded = false,
  canBeMaximized = false,
  showFavorites = false,
  displayTargets = ['webapp', 'kiosk'],
}: CreateStockImageRowParams): HTMLDivElement {
  const row = document.createElement('div');
  const loaderCls = getLoaderClass(imgTmbSize);
  const thumbActionText = getThumbnailActionText(imgTmbSize);
  const formattedDateStart = formatDateInputValue(dateStart);
  const formattedDateEnd = formatDateInputValue(dateEnd);
  const { allChecked, includeChecked, excludeChecked } = policyChecks;
  const rowRand = crypto.randomUUID().replaceAll('-', '').substring(0, 13);
  const startDateInputId = `stockImageStartDate_${uidRow}_${rowRand}`;
  const endDateInputId = `stockImageEndDate_${uidRow}_${rowRand}`;
  const webappChecked = getDisplayTargetChecked(displayTargets, 'webapp');
  const kioskChecked = getDisplayTargetChecked(displayTargets, 'kiosk');
  const availableForPurchaseChecked = getCheckedAttr(availableForPurchase);
  const watermarkChecked = getCheckedAttr(applyWatermark);
  const canBeDownloadedChecked = getCheckedAttr(canBeDownloaded);
  const canBeMaximizedChecked = getCheckedAttr(canBeMaximized);
  const showFavoritesChecked = getCheckedAttr(showFavorites);
  const positionInput = getPositionInputState(placement, position);

  row.dataset.uid = uidRow;
  row.classList.add('card', 'stock-image-card');
  row.innerHTML = buildStockImageRowInnerHtml({
    uidRow,
    imgUrl,
    thumbHtml,
    loaderCls,
    imgSize,
    thumbActionText,
    stockImageInfoHtml,
    startDateInputId,
    formattedDateStart,
    endDateInputId,
    formattedDateEnd,
    renderStyle,
    placement,
    positionInput,
    url,
    availableForPurchaseChecked,
    watermarkChecked,
    canBeDownloadedChecked,
    canBeMaximizedChecked,
    showFavoritesChecked,
    webappChecked,
    kioskChecked,
    rowRand,
    allChecked,
    includeChecked,
    excludeChecked,
    tagInputId,
    tags,
  });
  return row;
}

function activateTagsInput(tagInputId: string, tagValue: string) {
  $(`#${tagInputId}`).tagsInput(TAG_INPUT_PARAMS).importTags(tagValue);
}

// Set initial state
function initTagContainerState(stockImageRow: HTMLDivElement, isAllChecked: string) {
  const tagsContainer = stockImageRow.querySelector('.tags-input-container');
  if (isAllChecked && tagsContainer) tagsContainer.classList.add('disabled-tags-container');
}

function setupTagButtons(stockImageRow: HTMLDivElement, tagInptId: string) {
  const radioButtons = stockImageRow.querySelectorAll('.tag-policy-radio');
  radioButtons.forEach((radio) => {
    radio.addEventListener('change', function (this: HTMLInputElement) {
      const tagsInput = stockImageRow.querySelector(`#${tagInptId}`) as HTMLInputElement;
      const tagsContainer = stockImageRow.querySelector('.tags-input-container');

      if (this.value === 'all') {
        tagsInput.disabled = true;
        if (tagsContainer) tagsContainer.classList.add('disabled-tags-container');
      } else {
        tagsInput.disabled = false;
        if (tagsContainer) tagsContainer.classList.remove('disabled-tags-container');
      }
    });
  });
}

function parseStockImagePolicy(pol: TagPolicy) {
  const { inc, exc } = pol;
  const allChecked = !inc && !exc ? 'checked' : '';
  const includeChecked = !allChecked && inc && inc.length > 0 ? 'checked' : '';
  const excludeChecked = !allChecked && exc && exc.length > 0 ? 'checked' : '';
  const tagList = inc || exc || [];
  return {
    allChecked,
    includeChecked,
    excludeChecked,
    tagList,
  };
}

// eslint-disable-next-line no-undef
function toPolicyObject(tr: JQuery<HTMLElement>): TagPolicy {
  const tagPolicy = tr.find('input[type="radio"][name^="tagPolicy_"]:checked').val();
  const tagValue = String(tr.find('input[type="text"][data-role="tagsinput"]').val() ?? '');
  const tagList = tagValue.split(',');
  if (tagPolicy === 'include') return { inc: tagList };
  if (tagPolicy === 'exclude') return { exc: tagList };
  return {};
}

// Normalizes a URL entered by the user:
// - trims whitespace; returns '' for blank input
// - prepends 'https://' when no protocol is present
// Called at collection time so the stored value is always a valid absolute URL or empty.
function normalizeUrl(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  return `https://${trimmed}`;
}

function getStockImageList() {
  return document.getElementById(STOCK_IMAGE_LIST_ID);
}
